import { effect, inject, Injectable, Injector, signal } from '@angular/core';
import { DatabaseService } from './database.service';
import { LoggerService } from './logger.service';
import { SyncInitService } from './sync-init.service';
import { UserStorageService } from './user-storage.service';

export interface StateTransition {
  timestamp: Date;
  previousState: 'online' | 'offline';
  newState: 'online' | 'offline';
  syncStatus?: 'connected' | 'disconnected' | 'connecting';
}

export type ConnectionState = 'online' | 'offline' | 'connecting';

@Injectable({ providedIn: 'root' })
export class NetworkStateService {
  // Core state signals
  readonly isOnline = signal(window.navigator.onLine);
  readonly syncConnected = signal(false);
  readonly connectionState = signal<ConnectionState>(
    window.navigator.onLine ? 'online' : 'offline',
  );
  // Local diagnostic timeline of this device's own online/offline transitions -- never
  // compared against server data, so the local clock is fine.
  // eslint-disable-next-line no-restricted-syntax
  readonly lastOnlineTime = signal<Date | null>(window.navigator.onLine ? new Date() : null);
  readonly stayOfflineMode = signal(false);

  // Reconnection state
  readonly isReconnecting = signal(false);
  readonly reconnectAttempts = signal(0);

  // Activity tracking
  readonly stateTransitions = signal<StateTransition[]>([]);

  // Don't log initial state as a transition
  private hasInitialized = false;
  private reconnectTimeoutId?: number;
  private reconnectDelays = [1000, 2000, 4000, 8000, 16000, 30000]; // Exponential backoff with max 30s
  private db = inject(DatabaseService);
  private injector = inject(Injector);
  private _syncService?: SyncInitService;
  private logger = inject(LoggerService).getLogger('network');
  private userStorage = inject(UserStorageService);

  // Lazy getter to avoid circular dependency
  private get syncService(): SyncInitService | undefined {
    if (!this._syncService) {
      try {
        this._syncService = this.injector.get(SyncInitService);
      } catch {
        // Injection may fail - that's ok
        return undefined;
      }
    }
    return this._syncService;
  }

  constructor() {
    // Listen to browser's online/offline events
    window.addEventListener('online', () => this.handleOnline());
    window.addEventListener('offline', () => this.handleOffline());

    // Load stay offline preference from user-scoped storage with global fallback
    const savedPreference =
      this.userStorage.getTypedItem<boolean>('stayOfflineMode') ??
      this.userStorage.getGlobalTypedItem<boolean>('stayOfflineMode') ??
      false;
    if (savedPreference) {
      this.stayOfflineMode.set(true);
      this.logger.debug('Stay offline mode was active, sync connection will be skipped');
    }

    // Persist stay offline preference changes (both user-scoped and global)
    effect(() => {
      const value = this.stayOfflineMode();
      this.userStorage.setTypedItem('stayOfflineMode', value);
      this.userStorage.setGlobalTypedItem('stayOfflineMode', value);
    });

    // Note: syncConnected signal is updated manually when sync status changes
    // (removed automatic effect due to async nature of sync service methods)

    // Mark as initialized after setup
    setTimeout(() => {
      this.hasInitialized = true;
    }, 100);
  }

  private handleOnline(): void {
    const wasOffline = !this.isOnline();
    this.isOnline.set(true);
    // eslint-disable-next-line no-restricted-syntax -- local diagnostic, see lastOnlineTime above
    this.lastOnlineTime.set(new Date());

    // Log transition if initialized and actually changed state
    if (this.hasInitialized && wasOffline) {
      this.logStateTransition('offline', 'online');
    }

    // Auto-reconnect unless in stay offline mode
    if (!this.stayOfflineMode()) {
      this.attemptReconnection();
    } else {
      this.connectionState.set('online');
    }
  }

  private handleOffline(): void {
    const wasOnline = this.isOnline();
    this.isOnline.set(false);
    this.syncConnected.set(false);
    this.connectionState.set('offline');

    // Clear any pending reconnection attempts
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = undefined;
    }
    this.isReconnecting.set(false);
    this.reconnectAttempts.set(0);

    // Log transition if initialized and actually changed state
    if (this.hasInitialized && wasOnline) {
      this.logStateTransition('online', 'offline');
    }
  }

  private logStateTransition(
    previousState: 'online' | 'offline',
    newState: 'online' | 'offline',
  ): void {
    const transition: StateTransition = {
      // eslint-disable-next-line no-restricted-syntax -- local diagnostic, see lastOnlineTime above
      timestamp: new Date(),
      previousState,
      newState,
      syncStatus: this.syncConnected() ? 'connected' : 'disconnected',
    };

    // Add to in-memory log
    const currentLog = this.stateTransitions();
    this.stateTransitions.set([transition, ...currentLog].slice(0, 50)); // Keep last 50

    // Persist to SQLite network_activity_log table
    this.persistTransitionToDb(transition).catch((err) => {
      this.logger.error('Failed to persist network transition to database:', err);
    });
  }

  private async persistTransitionToDb(transition: StateTransition): Promise<void> {
    try {
      await this.db.run(
        `INSERT INTO network_activity_log (timestamp, previous_state, new_state, sync_status, metadata)
         VALUES (?, ?, ?, ?, ?)`,
        [
          transition.timestamp.toISOString(),
          transition.previousState,
          transition.newState,
          transition.syncStatus || null,
          '{}',
        ],
      );

      // Auto-purge entries older than 7 days
      await this.db.run(
        `DELETE FROM network_activity_log
         WHERE timestamp < datetime('now', '-7 days')`,
        [],
      );
    } catch (error) {
      this.logger.error('Error persisting network transition:', error);
      throw error;
    }
  }

  private attemptReconnection(): void {
    if (!this.isOnline() || this.stayOfflineMode()) {
      return;
    }

    this.connectionState.set('connecting');
    this.isReconnecting.set(true);

    const attempts = this.reconnectAttempts();
    const delayIndex = Math.min(attempts, this.reconnectDelays.length - 1);
    const delay = this.reconnectDelays[delayIndex];

    this.reconnectTimeoutId = window.setTimeout(() => {
      this.tryConnect();
    }, delay);
  }

  private async tryConnect(): Promise<void> {
    if (!this.isOnline() || this.stayOfflineMode()) {
      this.isReconnecting.set(false);
      this.reconnectAttempts.set(0);
      return;
    }

    try {
      // TODO: Replace with actual sync server connection check
      // For now, simulate connection check with a simple fetch
      const response = await fetch('/api/health', {
        method: 'GET',
        signal: AbortSignal.timeout(5000),
      });

      if (response.ok) {
        // Successfully connected
        this.syncConnected.set(true);
        this.connectionState.set('online');
        this.isReconnecting.set(false);
        this.reconnectAttempts.set(0);

        // Log successful reconnection
        if (this.hasInitialized) {
          this.logStateTransition('offline', 'online');
        }
      } else {
        throw new Error('Connection failed');
      }
    } catch (_error) {
      // Connection failed, retry with exponential backoff
      const nextAttempts = this.reconnectAttempts() + 1;
      this.reconnectAttempts.set(nextAttempts);

      if (this.isOnline() && !this.stayOfflineMode()) {
        this.attemptReconnection();
      } else {
        this.isReconnecting.set(false);
        this.connectionState.set('offline');
      }
    }
  }

  /**
   * Toggle stay offline mode
   * When enabled, prevents auto-reconnection even when network is available
   */
  toggleStayOfflineMode(): void {
    const newValue = !this.stayOfflineMode();
    this.stayOfflineMode.set(newValue);

    if (!newValue && this.isOnline() && !this.syncConnected()) {
      // Exiting stay offline mode while online - attempt to reconnect
      this.attemptReconnection();

      // Resume sync if it was paused
      if (this.syncService && this.syncService.isPaused()) {
        this.logger.debug('Resuming sync (exiting stay offline mode)');
        this.syncService.resume();
      }
    } else if (newValue) {
      // Entering stay offline mode - cancel any pending reconnection and pause sync
      if (this.reconnectTimeoutId) {
        clearTimeout(this.reconnectTimeoutId);
        this.reconnectTimeoutId = undefined;
      }
      this.isReconnecting.set(false);
      this.reconnectAttempts.set(0);
      this.connectionState.set(this.isOnline() ? 'online' : 'offline');

      // Pause sync if connected
      if (this.syncService && this.syncService.isConnected() && !this.syncService.isPaused()) {
        this.logger.debug('Pausing sync (entering stay offline mode)');
        this.syncService.pause();
      }
    }
  }

  /**
   * Manually trigger sync when in stay offline mode
   * Temporarily resumes sync, waits for changes to sync, then pauses again
   */
  async manualSync(): Promise<void> {
    if (!this.isOnline()) {
      throw new Error('Cannot sync while offline');
    }

    if (!this.stayOfflineMode()) {
      throw new Error('Manual sync only available in stay offline mode');
    }

    if (!this.syncService) {
      throw new Error('Sync service not initialized');
    }

    // Only allow manual sync if we have network connectivity
    if (!this.isOnline()) {
      this.logger.warn('Cannot perform manual sync - no network connection');
      throw new Error('No network connection. Manual sync requires internet access.');
    }

    // Temporarily resume sync for manual operation
    const wasConnected = this.syncService.isConnected();
    const wasPaused = this.syncService.isPaused();

    try {
      this.logger.debug('Manual sync requested');

      // If not connected yet (e.g., started in offline mode), connect now
      if (!wasConnected) {
        this.logger.debug('Connecting to sync server for manual sync...');
        await this.syncService.connect();
      } else if (wasPaused) {
        this.logger.debug('Resuming sync temporarily...');
        this.syncService.resume();
      }

      // Wait a moment for sync to catch up
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Return to previous state
      if (!wasConnected || this.stayOfflineMode()) {
        // If we connected just for this sync, or if offline mode is active, pause now
        this.logger.debug('Pausing sync after manual sync');
        this.syncService.pause();
      }

      this.logger.debug('Manual sync completed');
      await this.logManualSync('succeeded');
    } catch (error) {
      this.logger.error('Manual sync failed:', error);

      // Try to pause sync even if there was an error, but don't throw if pause fails
      try {
        if (this.syncService && this.syncService.isConnected()) {
          this.syncService.pause();
        }
      } catch (pauseError) {
        this.logger.error('Could not pause after error:', pauseError);
      }

      await this.logManualSync('failed');

      // Provide user-friendly error message
      let errorMessage: string;
      if (error instanceof Error) {
        // Check for WebSocket close error (broken connection)
        if (error.message.includes("Cannot read properties of null (reading 'close')")) {
          errorMessage = 'Sync connection is broken. Please exit offline mode and try again.';
        } else {
          errorMessage = error.message;
        }
      } else {
        errorMessage = 'Manual sync failed. The sync connection may be broken.';
      }

      throw new Error(errorMessage);
    }
  }

  private async logManualSync(status: 'succeeded' | 'failed'): Promise<void> {
    try {
      // network_activity_log is a local-only diagnostic table (not part of the synced
      // schema), so the local clock is fine here too.
      await this.db.run(
        `INSERT INTO network_activity_log (timestamp, previous_state, new_state, sync_status, metadata)
         VALUES (?, ?, ?, ?, ?)`,
        // eslint-disable-next-line no-restricted-syntax
        [new Date().toISOString(), 'manual_sync', 'manual_sync', status, '{}'],
      );
    } catch (error) {
      this.logger.error('Error logging manual sync:', error);
    }
  }

  /**
   * Get recent state transitions for display
   */
  getRecentTransitions(limit = 20): StateTransition[] {
    return this.stateTransitions().slice(0, limit);
  }

  /**
   * Load activity log from database
   */
  async loadActivityLogFromDb(limit = 50): Promise<StateTransition[]> {
    try {
      const rows = await this.db.exec(
        `SELECT timestamp, previous_state, new_state, sync_status
         FROM network_activity_log
         ORDER BY timestamp DESC
         LIMIT ?`,
        [limit],
      );

      return rows.map((row) => ({
        timestamp: new Date(row.timestamp as string),
        previousState: row.previous_state as 'online' | 'offline',
        newState: row.new_state as 'online' | 'offline',
        syncStatus: row.sync_status as 'connected' | 'disconnected' | 'connecting' | undefined,
      }));
    } catch (error) {
      this.logger.error('Error loading network activity log from database:', error);
      return [];
    }
  }

  /**
   * Clear activity log (for testing or user preference)
   */
  clearActivityLog(): void {
    this.stateTransitions.set([]);
  }

  /**
   * Cleanup on service destroy
   */
  destroy(): void {
    window.removeEventListener('online', () => this.handleOnline());
    window.removeEventListener('offline', () => this.handleOffline());
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
    }
  }
}
